import React, { useEffect, useState, useRef } from 'react';
import { z } from 'zod';
import { format, parseISO, addDays } from 'date-fns';
import { useForm } from 'react-hook-form';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  healthQuestionsSchemaWithConditional,
  HealthQuestionsSchema,
} from '@features/onboarding/schema/onboarding';

import { Form } from '@components/ui/form';

import { useFinalSubmissionStore } from 'src/store/final-submission';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useInsuranceStore } from 'src/store/insurance';
import { HealthIssueDetail } from 'src/services/interfaces/health';
import { Medication } from 'src/services/interfaces/application';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useHealthStore } from 'src/store/health';
// import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
// import { Application } from 'src/services/interfaces/application';
// import { onboardingSchema } from '@features/onboarding/schema/onboarding';

// import { MedicalConditionsIntroStep } from './components/medical-conditions-intro/MedicalConditionsIntroStep';
import { HealthConditionsSliderStep } from './components/health-conditions-slider/HealthConditionsSliderStep';
import { CoverageStartDateStep } from './components/coverage-start-date/CoverageStartDateStep';
import { PrivacyDisclosureStep } from './components/privacy-disclosure/PrivacyDisclosureStep';

import { getStepText } from './stepText';
import { HEALTH_QUESTIONS, REQUIRE_DETAILS } from './constants';
import { TakingMedicationsNowStep } from './components/medication-question/TakingMedicationsNowStep';
import { getHealthStepSchemaFromText } from './schema/phq';
import { InitialQuestionsStep } from './components/initial-questions/InitialQuestionsStep';
import { RiveSignatureAnimation } from './components/privacy-disclosure/RiveAnimation';
import { useApplicationStore } from 'src/store/application';
import { applicationService } from 'src/services/application';
import { HIDE_DETAILS_FOR } from '@features/onboarding/components/6-health-question-form/constants';
import { motion, AnimatePresence, cubicBezier, useReducedMotion } from 'framer-motion';

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};

/**
 * 🔀 Handles custom step skipping logic and synchronizes slider tab for onboarding steps.
 * Call this before default step navigation. Returns true if a custom switch was performed.
 * @param direction 'next' or 'prev' - direction of navigation
 */
function switchStepWithSliderSync({
  direction,
  currentStep,
  form,
  HEALTH_QUESTIONS,
  setCurrentStep,
}: {
  direction: 'next' | 'prev';
  currentStep: number;
  form: any;
  HEALTH_QUESTIONS: any[];
  setCurrentStep: (n: number) => void;
}): boolean {
  // Get step indexes
  const idxSurgeriesDiabetesHeartMental = HEALTH_QUESTIONS.findIndex(
    (q) => q.id === 'surgeriesDiabetesHeartMental'
  );
  const idxMedDetails = HEALTH_QUESTIONS.findIndex(
    (q) => q.id === 'medDetails'
  );
  const idxOtherHealthConditions = HEALTH_QUESTIONS.findIndex(
    (q) => q.id === 'otherHealthConditions'
  );
  const idxCoverageStart = HEALTH_QUESTIONS.findIndex(
    (q) => q.id === 'coverageStartDate'
  );

  // Get form values
  const initialQ1 = form.getValues(
    'healthQuestions.initialQuestions.treatment'
  );
  const selectedIssues =
    form.getValues('healthQuestions.surgeriesDiabetesHeartMental.conditions') ||
    [];
  const selectedConditions =
    form.getValues('healthQuestions.medDetails.medications') || [];

  // NEXT direction logic
  if (direction === 'next') {
    if (
      currentStep === idxSurgeriesDiabetesHeartMental &&
      selectedIssues[0]?.value === 'none' &&
      initialQ1 === false
    ) {
      setCurrentStep(idxCoverageStart);
      return true;
    }
    if (
      selectedIssues[0]?.value !== 'none' &&
      initialQ1 === false &&
      currentStep === idxMedDetails
    ) {
      if (!selectedConditions.length) {
        return false;
      }
      setCurrentStep(idxCoverageStart);
      return true;
    }
    if (
      currentStep === idxSurgeriesDiabetesHeartMental &&
      initialQ1 === true &&
      selectedIssues[0]?.value === 'none'
    ) {
      setCurrentStep(idxOtherHealthConditions);
      return true;
    }
  }
  // PREV direction logic
  if (direction === 'prev') {
    if (
      currentStep === idxCoverageStart &&
      initialQ1 === false &&
      selectedIssues[0]?.value === 'none'
    ) {
      setCurrentStep(idxSurgeriesDiabetesHeartMental);
      return true;
    }
    if (
      currentStep === idxCoverageStart &&
      initialQ1 === false &&
      selectedIssues[0]?.value !== 'none'
    ) {
      setCurrentStep(idxMedDetails);
      return true;
    }
    if (
      currentStep === idxOtherHealthConditions &&
      initialQ1 === true &&
      selectedIssues[0]?.value === 'none'
    ) {
      setCurrentStep(idxSurgeriesDiabetesHeartMental);
      return true;
    }
  }
  return false;
}

export const HealthQuestionForm = ({ onSubmit, onInvalid }: FormStepProps) => {
  const {
    data,
    isLastSelectedPHQ,
    setData,
    setLastSelectedPHQ,
    registerForm,
    unregisterForm,
  } = useOnboardingStore();
  const { handleJumpTo } = useFormControls();
  const { issues, conditions, issueDetails, setFailValidation } =
    useHealthStore();
  const [showAnimationScreen, setShowAnimationScreen] = useState(false);
  const { currentApplicationId } = useApplicationStore();
  
  // Move the hook call to the top level
  const pageTransitionVariants = usePageTransitionVariants();
  const idxPrivacyDisclosure = HEALTH_QUESTIONS.findIndex(
    (q) => q.id === 'privacyDisclosure'
  );
  const {
    isUnderwritingLoading,
    underwritingResult,
    underwritingError,
    isSubmittingApplication,
    runUnderwriting,
    getEstimation,
  } = useFinalSubmissionStore();
  const plans = useInsuranceStore((state) => state.plans);

  const form = useForm<HealthQuestionsSchema>({
    resolver: zodResolver(healthQuestionsSchemaWithConditional),
    defaultValues: {
      healthQuestions: {
        initialQuestions: {
          treatment:
            data.healthQuestions?.initialQuestions?.treatment ?? undefined,
          invalid: data.healthQuestions?.initialQuestions?.invalid ?? undefined,
          pregnancy:
            data.healthQuestions?.initialQuestions?.pregnancy ?? undefined,
        },
        takingMedicationsNow: {
          value: data.healthQuestions?.takingMedicationsNow?.value ?? undefined,
        },
        surgeriesDiabetesHeartMental: {
          conditions:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.conditions ??
            [],
          details:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.details ?? {},
        },
        medDetails: {
          medications: data.healthQuestions?.medDetails?.medications ?? [],
          details: data.healthQuestions?.medDetails?.details ?? {},
        },
        otherHealthConditions: {
          conditions:
            data.healthQuestions?.otherHealthConditions?.conditions ?? [],
          details: data.healthQuestions?.otherHealthConditions?.details ?? {},
        },
        finalHealthConditions: {
          value:
            data.healthQuestions?.finalHealthConditions?.value ?? undefined,
        },
        coverageStartDate: {
          effectiveDate:
            data.healthQuestions?.coverageStartDate?.effectiveDate ?? undefined,
        },
        privacyDisclosure: {
          disclaimer:
            data.healthQuestions?.privacyDisclosure?.disclaimer ?? false,
          signature: data.healthQuestions?.privacyDisclosure?.signature ?? '',
          accepted: data.healthQuestions?.privacyDisclosure?.accepted ?? false,
        },
      },
      ...data,
    },
    mode: 'onChange',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(null);

  const questionId = HEALTH_QUESTIONS[currentStep]?.id;
  const { title, description } = getStepText(questionId);

  useEffect(() => {
    if (isLastSelectedPHQ) {
      // setActiveCategoryIndex(planCategories.length - 1);
      setCurrentStep(idxPrivacyDisclosure);
      setLastSelectedPHQ(false);
    }
  }, [isLastSelectedPHQ, setCurrentStep, setLastSelectedPHQ, idxPrivacyDisclosure]);

  useEffect(() => {
    registerForm('healthQuestionForm', (data) => {
      form.reset(data);
    });

    return () => {
      unregisterForm('healthQuestionForm');
    };
  }, [form, registerForm, unregisterForm]);

  useEffect(() => {
    setStepErrorMessage(null);
    setFailValidation(false);
  }, [currentStep]);

  useEffect(() => {
    if (data.healthQuestions && Object.keys(data.healthQuestions).length > 0) {
      const formData = {
        healthQuestions: {
          initialQuestions: {
            treatment:
              data.healthQuestions.initialQuestions?.treatment ?? undefined,
            invalid:
              data.healthQuestions.initialQuestions?.invalid ?? undefined,
            pregnancy:
              data.healthQuestions.initialQuestions?.pregnancy ?? undefined,
          },
          takingMedicationsNow: {
            value:
              data.healthQuestions.takingMedicationsNow?.value ?? undefined,
          },
          surgeriesDiabetesHeartMental: {
            conditions:
              data.healthQuestions.surgeriesDiabetesHeartMental?.conditions ??
              [],
            details:
              data.healthQuestions.surgeriesDiabetesHeartMental?.details ?? {},
          },
          medDetails: {
            medications: data.healthQuestions.medDetails?.medications ?? [],
            details: data.healthQuestions.medDetails?.details ?? {},
          },
          otherHealthConditions: {
            conditions:
              data.healthQuestions.otherHealthConditions?.conditions ?? [],
            details: data.healthQuestions.otherHealthConditions?.details ?? {},
          },
          finalHealthConditions: {
            value:
              data.healthQuestions.finalHealthConditions?.value ?? undefined,
          },
          coverageStartDate: {
            effectiveDate:
              data.healthQuestions.coverageStartDate?.effectiveDate ??
              undefined,
          },
          privacyDisclosure: {
            disclaimer:
              data.healthQuestions.privacyDisclosure?.disclaimer ?? false,
            signature: data.healthQuestions.privacyDisclosure?.signature ?? '',
            accepted: data.healthQuestions.privacyDisclosure?.accepted ?? false,
          },
        },
        ...data,
      };

      const currentFormData = form.getValues();
      const currentSignature =
        currentFormData.healthQuestions?.privacyDisclosure?.signature;
      const newSignature =
        formData.healthQuestions?.privacyDisclosure?.signature;

      const signatureChanged = currentSignature !== newSignature;
      const otherDataChanged =
        JSON.stringify({
          ...currentFormData,
          healthQuestions: {
            ...currentFormData.healthQuestions,
            privacyDisclosure: {
              ...currentFormData.healthQuestions?.privacyDisclosure,
              signature: currentSignature,
            },
          },
        }) !==
        JSON.stringify({
          ...formData,
          healthQuestions: {
            ...formData.healthQuestions,
            privacyDisclosure: {
              ...formData.healthQuestions?.privacyDisclosure,
              signature: newSignature,
            },
          },
        });

      if (signatureChanged || otherDataChanged) {
        form.reset(formData);
      }
    }
  }, [data]);

  const isCurrentStepValid = (currentStep: number, formData: any): boolean => {
    const question = HEALTH_QUESTIONS[currentStep];
    if (!question?.id) return false;

    const questionId = question.id;

    const stepSchema = getHealthStepSchemaFromText(questionId);
    if (!stepSchema) {
      setStepErrorMessage(null);
      return true;
    }

    const stepData = {
      healthQuestions: {
        [questionId]: formData.getValues(`healthQuestions.${questionId}`),
      },
    };

    try {
      stepSchema.parse(stepData);
      setStepErrorMessage(null);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setStepErrorMessage(
          err.errors[0]?.message || 'Please complete the current step.'
        );
      } else {
        setStepErrorMessage('Please complete the current step.');
      }
      return false;
    }
  };

  const handleNext = () => {
    const nextIndex = currentStep + 1;
    const nextQuestion = HEALTH_QUESTIONS[nextIndex]?.id;

    if (nextQuestion === 'currentMeds') {
      setCurrentStep(nextIndex + 1);
      return;
    }

    if (currentStep < HEALTH_QUESTIONS.length - 1) {
      setCurrentStep(nextIndex);
    } else {
      setLastSelectedPHQ(true);
      handleJumpTo('Finish Up');
    }
  };

  const handlePrevious = () => {
    const prevIndex = currentStep - 1;

    const currentId = HEALTH_QUESTIONS[currentStep]?.id;
    const prevQuestion = HEALTH_QUESTIONS[prevIndex]?.id;

    // if (
    //   currentId === 'medicalConditionsIntro' &&
    //   prevQuestion === 'currentMeds'
    // ) {
    //   setCurrentStep(prevIndex - 1);
    //   return;
    // }

    if (currentStep > 0) {
      setCurrentStep(prevIndex);
    }
  };

  const validationOtherHealthConditions1 = () => {
    const activeTabs =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) ?? [];
    const fields =
      form.getValues('healthQuestions.medDetails.medications') ?? [];

    const selectedAllElems = activeTabs.filter(e => e.value !== 'none').map(e => e.value);
    const selectedGroups = selectedAllElems.filter(e => HIDE_DETAILS_FOR.includes(e));

    if (selectedGroups.length === 0) {
      return true;
    }

    const onlyGroupValues = selectedAllElems.filter(e => !HIDE_DETAILS_FOR.includes(e));
    const fieldsByGroup: Record<string, any[]> = {};
    onlyGroupValues.forEach(field => {
      if (field) {
        const [groupName] = field.split('-');
        if (groupName && selectedGroups.includes(groupName)) {
          if (!fieldsByGroup[groupName]) {
            fieldsByGroup[groupName] = [];
          }
          fieldsByGroup[groupName].push(field);
        }
      }
    });

    for (const group of selectedGroups) {
      const groupFields = fieldsByGroup[group] || [];
      if (groupFields.length === 0) {
        return true;
      }
    }

    if (!REQUIRE_DETAILS) return false;

    type HealthIssueDetails = HealthIssueDetail & {
      parent?: string;
    };

    type GroupedHealthIssueDetails = {
      [parent: string]: HealthIssueDetails[];
    };

    const fieldsDetail: GroupedHealthIssueDetails =
      fields.reduce<GroupedHealthIssueDetails>((acc, e) => {
        const [parent, key] = e.value.split('-');
        const detail = issueDetails.find(
          (d) => d.key === key
        ) as HealthIssueDetails;

        if (detail && key !== 'none') {
          detail.parent = parent;
          if (!acc[parent]) {
            acc[parent] = [];
          }
          acc[parent].push(detail);
        } else if (key === 'none') {
          if (!acc[parent]) {
            acc[parent] = [];
          }
          acc[parent].push({
            key: 'none',
            label: 'NONE',
            issueId: NaN,
            type: NaN,
          });
        }

        return acc;
      }, {});

    for (let field of activeTabs) {
      if (!fieldsDetail[field.value]) {
        return true;
      }
      const groupFields = fields.filter((f) =>
        f.value.startsWith(`${field.value}-`)
      );
      for (let groupField of groupFields) {
        if (!groupField.value.endsWith('-none')) {
          if (!groupField?.details || groupField?.details.trim() === '') {
            return true;
          }
        }
      }
    }
    return false;
  };
  const validationTakingMedicationsNow = () => {
    // @ts-ignore
    const fieldsMedications = (form.getValues('healthQuestions.takingMedicationsNow.medications') as Medication[]) ?? [];
    const isTakingMedications = form.getValues('healthQuestions.takingMedicationsNow.value');
    const hasInvalidFields = fieldsMedications?.some((field) => {
      const missingCore = !field.applicant || !field.dosage || !field.frequency;
      const missingDesc = !field.description;
      return REQUIRE_DETAILS ? missingCore || missingDesc : missingCore;
    });
    if (isTakingMedications && !fieldsMedications.length) {
      setFailValidation(true);
      return true;
    }
    return hasInvalidFields;
  };

  const localValidation = () => {
    const medDetails = HEALTH_QUESTIONS.findIndex((q) => q.id === 'medDetails');
    const idxTakingMedicationsNow = HEALTH_QUESTIONS.findIndex(
      (q) => q.id === 'takingMedicationsNow'
    );
    const idxOtherHealthConditions = HEALTH_QUESTIONS.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );

    const isInvalidTakingMedications =
      currentStep === idxTakingMedicationsNow
        ? validationTakingMedicationsNow()
        : false;
    const isInvalidOtherHealthConditions =
      currentStep === medDetails ? validationOtherHealthConditions1() : false;
    const isStepInvalid = !isCurrentStepValid(currentStep, form);
    const isInvalidDetails =
      currentStep === idxOtherHealthConditions ? validateDetails() : false;

    const isNotValid =
      isInvalidTakingMedications ||
      isInvalidOtherHealthConditions ||
      isStepInvalid ||
      isInvalidDetails ||
      switchStepWithSliderSync({
        direction: 'next',
        currentStep,
        form,
        HEALTH_QUESTIONS,
        setCurrentStep,
      });

    return !!isNotValid;
  };

  const validateDetails = () => {
    if (!REQUIRE_DETAILS) return false;
    const conditions =
      form.getValues('healthQuestions.otherHealthConditions.conditions') ?? [];
    const details =
      form.getValues('healthQuestions.otherHealthConditions.details') ?? {};

    for (const condition of conditions) {
      const value = condition.value;
      if (!HIDE_DETAILS_FOR.includes(value) && value !== 'none') {
        const detailValue = details[value];
        if (!detailValue || detailValue.trim() === '') {
          return true;
        }
      }
    }
    return false;
  };

  const onNextClick = async () => {
    if (localValidation()) {
      setFailValidation(true);
      return;
    }

    setFailValidation(false);

    const formData = form.getValues();

    setData(formData);

    if (HEALTH_QUESTIONS[currentStep]?.id === 'privacyDisclosure') {
      const requestFormData = transformApplicationData(formData, plans);

      setShowAnimationScreen(true);
      const applicationState = await applicationService.applicationState();

      if (!applicationState?.phqAcceptedAt) {
        try {
          if (currentApplicationId) {
            await applicationService.updateApplicationById(
              currentApplicationId,
              requestFormData
            );
            await getEstimation(currentApplicationId);
          } else {
            await runUnderwriting(requestFormData);
          }
        } catch (error) {
          console.error('Error during estimation:', error);
          setShowAnimationScreen(false);
          return;
        }
      }

      setTimeout(async () => {
        setShowAnimationScreen(false);
        setLastSelectedPHQ(true);
        handleJumpTo('Finish Up');
      }, 3000);
    } else {
      handleNext();
    }
  };
  const renderAnimationScreen = () => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-transparent">
        <RiveSignatureAnimation
          riveFileUrl="/anims/loader2.riv"
          width={300}
          height={150}
        />
      </div>
    );
  };

  const onPrevClick = async () => {
    setFailValidation(false);
    if (
      switchStepWithSliderSync({
        direction: 'prev',
        currentStep,
        form,
        HEALTH_QUESTIONS,
        setCurrentStep,
      })
    ) {
      return;
    }

    handlePrevious();
  };

  const renderStep = () => {
    const question = { ...HEALTH_QUESTIONS[currentStep] };
    const errorMessage = stepErrorMessage;

    if (question.id === 'coverageStartDate') {
      const effectiveDate = form.getValues(
        'healthQuestions.coverageStartDate.effectiveDate'
      );
      if (effectiveDate) {
        const options = question.options as { label: string; value: string }[];
        const dateExists = options.some((opt) => opt.value === effectiveDate);

        if (!dateExists) {
          const date = parseISO(effectiveDate);
          const adjustedDate = addDays(date, 1);

          question.options = [
            ...options,
            {
              value: effectiveDate,
              label: format(adjustedDate, 'MMMM d, yyyy'),
            },
          ];
        }
      }
    }

    const stepIds = [
      'surgeriesDiabetesHeartMental',
      'medDetails',
      'otherHealthConditions',
      'finalHealthConditions',
    ];

    let questionsToPass = HEALTH_QUESTIONS.filter((q) =>
      stepIds.includes(q.id)
    );

    if (issues.length > 0) {
      questionsToPass = [...questionsToPass];
      const surgeriesIdx = questionsToPass.findIndex(
        (q) => q.id === 'surgeriesDiabetesHeartMental'
      );
      if (surgeriesIdx !== -1) {
        questionsToPass[surgeriesIdx] = {
          ...questionsToPass[surgeriesIdx],
          options: [
            ...issues.map((v) => ({ label: v.label, value: v.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    if (conditions.length > 0) {
      const condIdx = questionsToPass.findIndex(
        (q) => q.id === 'otherHealthConditions'
      );
      if (condIdx !== -1) {
        questionsToPass[condIdx] = {
          ...questionsToPass[condIdx],
          options: [
            ...conditions.map((c) => ({ label: c.label, value: c.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    const detailsIdx = questionsToPass.findIndex((q) => q.id === 'medDetails');
    const selectedIssues =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) || [];
    if (detailsIdx !== -1 && issues.length > 0 && issueDetails.length > 0) {
      const grouped = issues
        .filter((issue) =>
          selectedIssues.map((v) => v.value).includes(issue.key)
        )
        .map((issue) => [
          ...issueDetails
            .filter((d) => d.issueId === issue.id)
            .map((d) => ({ label: d.label, value: d.key })),
          {
            label:
              "<strong>NONE APPLY</strong> – I've read and understood the conditions above.",
            value: 'none',
            isNoneApply: true,
          },
        ]);
      questionsToPass[detailsIdx] = {
        ...questionsToPass[detailsIdx],
        options: grouped,
      };
    }

    const activeTab = questionsToPass.findIndex(
      (e) => e.id === HEALTH_QUESTIONS[currentStep].id
    );

    switch (question.id) {
      case 'initialQuestions':
        return <InitialQuestionsStep form={form} question={question} />;
      case 'takingMedicationsNow':
        return <TakingMedicationsNowStep form={form} question={question} />;
      // case 'medicalConditionsIntro':
      //   return <MedicalConditionsIntroStep form={form} question={question} />;
      case 'surgeriesDiabetesHeartMental':
      case 'medDetails':
      case 'otherHealthConditions':
      case 'finalHealthConditions':
        return (
          <HealthConditionsSliderStep
            questions={questionsToPass}
            activeTab={activeTab}
            form={form}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'coverageStartDate':
        return (
          <CoverageStartDateStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'privacyDisclosure':
        return (
          <PrivacyDisclosureStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Form {...form}>
      <OnboardingFormStepLayout
        handleFormSubmit={onNextClick}
        onPrev={currentStep > 0 ? onPrevClick : undefined}
        title={title}
        description={description}
        isFormValid={true}
        isLoading={
          showAnimationScreen ||
          isUnderwritingLoading ||
          isSubmittingApplication
        }
        isAnimationScreen={showAnimationScreen}
        backgroundClassName={showAnimationScreen ? 'bg-white' : undefined}
        headerAnimationKey={`health-questions-${currentStep}-${HEALTH_QUESTIONS[currentStep]?.id}`}
        contentAnimationKey={`health-questions-content-${currentStep}-${HEALTH_QUESTIONS[currentStep]?.id}`}
      >
        <div className="flex w-full rounded-2xl min-h-full max-2xl:mt-2 max-2xl:mb-6">
          <div className="flex-1 relative min-h-full">
            {showAnimationScreen ? (
              renderAnimationScreen()
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={HEALTH_QUESTIONS[currentStep].id}
                  variants={pageTransitionVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="h-full"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </OnboardingFormStepLayout>
    </Form>
  );
};
